import React, { useEffect, useState } from "react";
import { apiGet } from "../../services/api";
import Button from "../common/Button";

interface Supplier {
  id: number;
  name: string;
  mobile?: string;
  balance_due?: number;
}

interface LedgerRow {
  date?: string;
  type?: string;
  ref?: string;
  receipt_no?: string;
  receipt_number?: string;
  debit?: string | number;
  credit?: string | number;
  balance?: number;
}

interface Bill {
  id: number;
  invoice_no?: string;
  date?: string;
  total?: number;
  paid?: number;
  balance?: number;
  items_count?: number;
  items_preview?: string[];
}

interface BillItem {
  drug_id: number;
  drug_name: string;
  strip_cost: number;
  units_per_strip: number;
  num_strips: number;
  total_units: number;
  cost_per_unit: number;
  line_total: number;
  batch_number: string;
  tax_percentage: number;
  tax_amount: number;
}

export default function PharmacySupplierLedger({
  supplier,
  onPayBill,
  refreshTrigger
}: {
  supplier: Supplier | null,
  onPayBill?: (bill: Bill) => void,
  refreshTrigger?: any
}) {
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState<Bill[]>([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [showPaidBills, setShowPaidBills] = useState(true);
  const [activeTab, setActiveTab] = useState<"ledger" | "bills">("ledger");

  const [viewingItems, setViewingItems] = useState<number | null>(null);
  const [billItems, setBillItems] = useState<BillItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  useEffect(() => {
    if (!supplier?.id) {
      setRows([]);
      return;
    }
    setLoading(true);
    apiGet(`/api/pharmacy/supplier/${supplier.id}/ledger`)
      .then((res: unknown) => setRows(Array.isArray(res) ? (res as LedgerRow[]) : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [supplier, refreshTrigger]);

  useEffect(() => {
    if (!supplier?.id) {
      setBills([]);
      return;
    }
    setBillsLoading(true);
    // Always fetch all bills; UI toggle controls visibility.
    apiGet(`/api/pharmacy/supplier/${supplier.id}/bills?include_paid=true`)
      .then((res: unknown) => setBills(Array.isArray(res) ? (res as Bill[]) : []))
      .catch(() => setBills([]))
      .finally(() => setBillsLoading(false));
  }, [supplier, refreshTrigger]);

  useEffect(() => {
    // Close items modal if supplier changes
    setViewingItems(null);
    setBillItems([]);
  }, [supplier?.id]);

  const formatINR = (value: unknown) => {
    const n = Number(value ?? 0);
    if (!Number.isFinite(n)) return "—";
    return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  };

  const formatDate = (raw: unknown) => {
    const s = String(raw ?? "");
    if (!s) return "—";
    // Handles "YYYY-MM-DD" and ISO timestamps
    return s.slice(0, 10);
  };

  const supplierDue =
    Number(supplier?.balance_due ?? 0) ||
    Number(bills.reduce((sum, b) => sum + Number(b.balance || 0), 0));
  const unpaidCount = bills.filter((b) => Number(b.balance || 0) > 0).length;
  const totalBills = bills.length;
  const visibleBills = showPaidBills ? bills : bills.filter((b) => Number(b.balance || 0) > 0);

  const viewItems = async (billId: number) => {
    if (!supplier?.id) return;
    try {
      setItemsLoading(true);
      const items = await apiGet(`/api/pharmacy/supplier/${supplier.id}/bills/${billId}/items`);
      setBillItems(Array.isArray(items) ? (items as BillItem[]) : []);
      setViewingItems(billId);
    } catch (e) {
      console.error("Failed to load supplier bill items", e);
      setBillItems([]);
      setViewingItems(billId);
    } finally {
      setItemsLoading(false);
    }
  };

  if (!supplier) return null;
  return (
    <div className="mod-standalone-window" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '600px', backgroundColor: '#f8fafc', padding: '16px', gap: '16px' }}>
      <div className="mod-card" style={{ padding: '24px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '20px', background: 'linear-gradient(to right, #ffffff, #f1f5f9)' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🏢 {supplier.name}
          </h2>
          <div style={{ color: '#64748b', marginTop: '4px', fontSize: '0.95rem' }}>
            {supplier.mobile ? `📞 ${supplier.mobile}` : "Pharmacy supplier"}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 20px', minWidth: '120px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Balance Due</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: supplierDue > 0 ? '#ef4444' : '#10b981' }}>{formatINR(supplierDue)}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 20px', minWidth: '100px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Total Bills</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a' }}>{totalBills}</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 20px', minWidth: '100px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Unpaid Bills</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: unpaidCount > 0 ? '#f59e0b' : '#10b981' }}>{unpaidCount}</div>
          </div>
        </div>
      </div>

      <div className="mod-sub-nav" style={{ borderBottom: '1px solid #e2e8f0' }}>
        <button
          type="button"
          className={`mod-sub-nav-item ${activeTab === "ledger" ? "active" : ""}`}
          onClick={() => setActiveTab("ledger")}
          style={{ padding: '12px 24px' }}
        >
          📒 Ledger Flow
        </button>
        <button
          type="button"
          className={`mod-sub-nav-item ${activeTab === "bills" ? "active" : ""}`}
          onClick={() => setActiveTab("bills")}
          style={{ padding: '12px 24px' }}
        >
          🧾 Invoice Bills
        </button>

        <div style={{ flex: 1 }} />

        {activeTab === "bills" ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#475569', fontWeight: 500, paddingRight: '12px' }}>
            <input type="checkbox" checked={showPaidBills} onChange={(e) => setShowPaidBills(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
            Show settled bills
          </label>
        ) : null}
      </div>

      {activeTab === "ledger" ? (
        <div className="mod-card" style={{ flex: 1, padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading ledger history…</div>
          ) : rows.length === 0 ? (
            <div className="mod-empty-state" style={{ margin: '20px', border: '1px dashed #cbd5e1' }}>No transactions found for this supplier</div>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <table className="dt-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Ref</th>
                    <th>Receipt</th>
                    <th style={{ textAlign: 'right' }}>Debit (Payment)</th>
                    <th style={{ textAlign: 'right' }}>Credit (Bill)</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const receipt = r.receipt_no || r.receipt_number || "—";
                    const debitVal = r.debit === null || r.debit === undefined ? null : r.debit;
                    const creditVal = r.credit === null || r.credit === undefined ? null : r.credit;
                    return (
                      <tr key={i}>
                        <td style={{ color: '#475569', fontSize: '0.9rem' }}>{formatDate(r.date)}</td>
                        <td style={{ fontWeight: 500, color: '#334155' }}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', background: r.type === 'Payment' ? '#dcfce7' : r.type === 'Bill' ? '#fef08a' : '#f1f5f9', color: r.type === 'Payment' ? '#166534' : r.type === 'Bill' ? '#854d0e' : '#475569' }}>
                            {r.type || "—"}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>{r.ref || "—"}</td>
                        <td style={{ fontSize: '0.85rem' }}>{receipt}</td>
                        <td style={{ textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{debitVal === null ? "—" : formatINR(debitVal)}</td>
                        <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>{creditVal === null ? "—" : formatINR(creditVal)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>{formatINR(r.balance ?? 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="mod-card" style={{ flex: 1, padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {billsLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading bills…</div>
          ) : visibleBills.length === 0 ? (
             <div className="mod-empty-state" style={{ margin: '20px', border: '1px dashed #cbd5e1' }}>No bills in history</div>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <table className="dt-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Paid</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                    <th>Items</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleBills.map((b) => (
                    <tr key={b.id}>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{b.invoice_no || "—"}</td>
                      <td style={{ color: '#64748b', fontSize: '0.9rem' }}>{formatDate(b.date)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#334155' }}>{formatINR(b.total || 0)}</td>
                      <td style={{ textAlign: 'right', color: '#16a34a' }}>{formatINR(b.paid || 0)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: Number(b.balance || 0) > 0 ? '#dc2626' : '#10b981' }}>
                        {formatINR(b.balance || 0)}
                      </td>
                      <td style={{ fontSize: '0.85rem', color: '#64748b' }}>
                        {(b.items_preview ?? []).length > 0 ? (
                          <span title={(b.items_preview ?? []).join(", ")}>
                            {(b.items_preview ?? []).join(", ")}
                            {(b.items_count ?? 0) > 3 ? "…" : ""}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: "flex", gap: '8px', justifyContent: "flex-end" }}>
                          {(b.items_count ?? 0) > 0 && (
                            <Button variant="outline" size="sm" onClick={() => viewItems(b.id)}>
                              View details
                            </Button>
                          )}
                          {(b.balance ?? 0) > 0 && onPayBill && (
                            <Button variant="primary" size="sm" onClick={() => onPayBill(b)}>
                              Pay Now
                            </Button>
                          )}
                          {(b.items_count ?? 0) === 0 && (b.balance ?? 0) === 0 && <span style={{ color: '#94a3b8' }}>—</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {viewingItems && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setViewingItems(null)}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '24px', width: '100%', maxWidth: '850px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.25rem' }}>Supplier Bill Items</h3>
              <Button variant="ghost" onClick={() => setViewingItems(null)} style={{ fontSize: '1.5rem', padding: '0 8px' }}>×</Button>
            </div>
            {itemsLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading items…</div>
            ) : (
              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead style={{ background: '#f8fafc' }}>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Drug Name</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Batch #</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Strips</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569', textAlign: 'right' }}>MRP (Pack)</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569', textAlign: 'right' }}>GST %</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Tax</th>
                      <th style={{ padding: '12px', fontSize: '0.85rem', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billItems.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>No items found in this bill</td>
                      </tr>
                    ) : (
                      billItems.map((it, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px', fontSize: '0.9rem', color: '#0f172a', fontWeight: 500 }}>{it.drug_name}</td>
                          <td style={{ padding: '12px', fontSize: '0.85rem', color: '#64748b' }}>{it.batch_number || "—"}</td>
                          <td style={{ padding: '12px', fontSize: '0.9rem', color: '#334155', textAlign: 'right' }}>{it.num_strips}</td>
                          <td style={{ padding: '12px', fontSize: '0.9rem', color: '#334155', textAlign: 'right' }}>₹{Number(it.strip_cost || 0).toFixed(2)}</td>
                          <td style={{ padding: '12px', fontSize: '0.9rem', color: '#334155', textAlign: 'right' }}>{it.tax_percentage}%</td>
                          <td style={{ padding: '12px', fontSize: '0.9rem', color: '#334155', textAlign: 'right' }}>₹{Number(it.tax_amount || 0).toFixed(2)}</td>
                          <td style={{ padding: '12px', fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', textAlign: 'right' }}>₹{Number(it.line_total || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setViewingItems(null)}>Close Window</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
